import React from "react";
import { motion } from "framer-motion";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-100 py-12 px-6 md:px-20">
      <div className="max-w-5xl mx-auto bg-white shadow-lg rounded-lg p-10">
        {/* Page Heading */}
        <motion.h1
          className="text-4xl font-extrabold text-purple-700 text-center mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Privacy Policy for Pawdash
        </motion.h1>

        <p className="text-gray-500 text-center mb-8">
          Last Updated: <strong>June 23, 2025</strong>
        </p>

        <motion.section
          className="space-y-8 text-gray-700"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          {/* Introduction */}
          <div>
            <p>
              Pawdash ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy outlines how we collect, use, disclose, and safeguard your information when you use our website and mobile application (collectively, the "Services") within the United Arab Emirates (UAE).
            </p>
          </div>

          {/* 1. Information We Collect */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">1. Information We Collect</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Personal Information:</strong> Includes your name, contact details, address, payment information, and other information that identifies you as an individual.
              </li>
              <li>
                <strong>Pet Information:</strong> Details about your pet, such as breed, age, health conditions, and preferences, to facilitate our Services.
              </li>
              <li>
                <strong>Technical Information:</strong> Includes your IP address, browser type, operating system, device identifiers, and other technical data related to your use of the Services.
              </li>
              <li>
                <strong>Usage Data:</strong> Information on how you interact with our Services, including pages viewed, links clicked, and time spent on each page.
              </li>
              <li>
                <strong>Location Data:</strong> With your consent, we may collect your geolocation data to offer location-specific services, such as pet delivery and walking.
              </li>
              <li>
                <strong>Notification Data:</strong> With your permission, we collect data related to your notification preferences to provide timely updates on orders, promotional offers, and other relevant information.
              </li>
            </ul>
          </div>

          {/* 2. How We Use Your Information */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">2. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Provide and Enhance Services:</strong> Process requests, manage accounts, and deliver the requested pet services, such as delivery and walking.
              </li>
              <li>
                <strong>Customer Support:</strong> Respond to inquiries, troubleshoot issues, and improve customer service.
              </li>
              <li>
                <strong>Personalization:</strong> Customize your experience based on your preferences and pet’s needs.
              </li>
              <li>
                <strong>Billing and Payments:</strong> Process payments for services rendered and handle billing-related inquiries.
              </li>
              <li>
                <strong>Marketing and Communications:</strong> Send promotional information, updates, and special offers, with your consent.
              </li>
              <li>
                <strong>Location-Based Services:</strong> Use your location data, with your consent, to process orders and provide services specific to your area, such as pet delivery and walking.
              </li>
              <li>
                <strong>Notifications:</strong> Send timely updates on your preferred orders, promotional offers, and other relevant information, with your permission for notifications.
              </li>
              <li>
                <strong>Security and Compliance:</strong> Safeguard the Services against fraud, abuse, and security threats, and comply with legal obligations.
              </li>
            </ul>
          </div>

          {/* 3. Sharing Your Information */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">3. Sharing Your Information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Service Providers:</strong> We share information with third-party providers who help deliver our Services (e.g., payment processors, data analytics providers, delivery partners).
              </li>
              <li>
                <strong>Business Partners:</strong> With your consent, we may share limited data with select partners offering complementary services.
              </li>
              <li>
                <strong>Legal Requirements:</strong> We may disclose information to comply with legal obligations, court orders, or regulatory requirements.
              </li>
              <li>
                <strong>Business Transfers:</strong> If we are involved in a merger, acquisition, or asset sale, your information may be transferred as part of the transaction.
              </li>
            </ul>
          </div>

          {/* 4. Data Retention */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">4. Data Retention</h2>
            <p>
              We retain your information only as long as necessary to fulfill the purposes outlined in this policy or to comply with legal requirements. When no longer needed, your data will be securely deleted or anonymized.
            </p>
          </div>

          {/* 5. Data Security */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">5. Data Security</h2>
            <p>
              We employ industry-standard security measures to protect your information from unauthorized access, disclosure, alteration, and destruction. However, no method of transmission over the internet is completely secure, and we cannot guarantee absolute security.
            </p>
          </div>

          {/* 6. Your Rights and Choices */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">6. Your Rights and Choices</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Access and Correction:</strong> You can review and update your personal information through your account settings or by contacting us.
              </li>
              <li>
                <strong>Deletion:</strong> You may request that we delete your data, subject to legal limitations.
              </li>
              <li>
                <strong>Opt-Out:</strong> You may opt out of receiving marketing communications by following the unsubscribe instructions or contacting us directly.
              </li>
              <li>
                <strong>Data Portability:</strong> In certain cases, you may have the right to request a copy of your personal information in a structured format.
              </li>
              <li>
                <strong>Location Permission:</strong> You can enable or disable location services for Pawdash through your device settings. Disabling location services may limit our ability to provide location-specific services.
              </li>
              <li>
                <strong>Notification Permission:</strong> You can enable or disable notifications for Pawdash through your device settings. Disabling notifications may prevent you from receiving timely updates on orders and offers.
              </li>
            </ul>
          </div>

          {/* 7. Use of Cookies and Tracking Technologies */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">7. Use of Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar technologies to collect information about your usage and preferences on our Services. You can control cookies through your browser settings, but disabling cookies may impact your experience.
            </p>
          </div>

          {/* 8. Third-Party Links and Services */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">8. Third-Party Links and Services</h2>
            <p>
              Our Services may contain links to third-party websites or services that are not operated by Pawdash. This Privacy Policy does not cover the practices of third-party sites, and we encourage you to review their privacy policies before providing any personal information.
            </p>
          </div>

          {/* 9. Advertising */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">9. Advertising</h2>
            <p>
              We may allow third-party advertising partners to display ads within our Services. These partners may collect information to provide relevant advertisements. Please review our partners' privacy policies to understand their data practices.
            </p>
          </div>

          {/* 10. Children’s Privacy */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">10. Children’s Privacy</h2>
            <p>
              Our Services are not intended for children under 13. We do not knowingly collect personal information from children. If we become aware of any such data, we will take steps to delete it.
            </p>
          </div>

          {/* 11. Changes to This Privacy Policy */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically to reflect changes in our practices, technology, or legal requirements. Any updates will be posted on this page, and we will notify you of significant changes. Your continued use of the Services after updates signifies your acceptance of the revised policy.
            </p>
          </div>

          {/* 12. Contact Us */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">12. Contact Us</h2>
            <ul className="list-disc pl-6">
              <li><strong>Email:</strong> info@mypawdash.com</li>
              <li><strong>Address:</strong> Abu Dhabi-UAE</li>
              <li><strong>Phone:</strong> +971 509 921 927</li>
            </ul>
          </div>
        </motion.section>

        {/* Back to Home Button */}
        <div className="mt-8 flex justify-center">
          <motion.a
            href="/"
            className="px-6 py-3 bg-purple-700 text-white text-lg rounded-lg shadow-lg hover:bg-purple-800 transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Back to Home
          </motion.a>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
